import { copy, countCopy, escapeHtml, formatCopy, language } from "./runtime.js";
import { MAP_LAB_MIN_POPULATION, MAP_LAB_POINT_TITLE_KEYS } from "./constants.js";
import { mapLabMagnitudeMetric, mapLabZipDetail } from "./data.js";
import { sickRateCssColor } from "./shading.js";
import { formatReportMapDate } from "../../map-time-window.js";

// PREVIEW: click a single-ZIP centroid dot for its counts; pointer cursor on hover.
// Shared by the clustered single-ZIP markers and the flat "Not clustered" dots.

export function wireZipMarkerPopup(map, layerId) {
  map.on("click", layerId, (event) => {
    const feature = event.features && event.features[0];
    if (!feature || !window.mapboxgl) return;
    new window.mapboxgl.Popup({ closeButton: true, maxWidth: "240px" })
      .setLngLat(popupAnchor(feature, event))
      .setHTML(zipMarkerPopupHtml(feature.properties))
      .addTo(map);
  });
  map.on("mouseenter", layerId, () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", layerId, () => {
    map.getCanvas().style.cursor = "";
  });
}

// PREVIEW: anchor the popup to the dot's own centroid (Point features) so it opens
// centered on the marker, not wherever inside the dot the click landed. Polygon
// clicks fall back to the click point, which is the expected behavior for an area.
export function popupAnchor(feature, event) {
  const geometry = feature.geometry;
  if (geometry && geometry.type === "Point" && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates;
  }
  return event.lngLat;
}

// PREVIEW helper: active time-range index (defaults to 1 month), clamped to a valid slot.

export function zipMarkerPopupHtml(props) {
  const total = Number(props.total_count) || 0;
  const sick = Number(props.sick_count) || 0;
  const healthy = Number(props.healthy_count) || 0;
  const peopleSick = Number(props.people_sick_count) || 0;
  const latest = formatReportDate(props.latest_submitted_at);
  const totalText = reportCountText(total);
  return `
    <div class="zip-popup">
      <strong>${escapeHtml(copy("map_zip"))} ${escapeHtml(props.zip_code)}</strong>
      <div class="zip-popup-total">${escapeHtml(totalText)}</div>
      <div><span class="zip-popup-sick">${sick} ${escapeHtml(copy("map_sick"))}</span> &middot; <span class="zip-popup-healthy">${healthy} ${escapeHtml(copy("map_healthy_lower"))}</span></div>
      <div class="zip-popup-sub">${escapeHtml(countCopy(
        "map_people_reported_sick_one",
        "map_people_reported_sick_many",
        peopleSick
      ))}</div>
      ${latest ? `<div class="zip-popup-sub">${escapeHtml(formatCopy("map_latest_report", { date: latest }))}</div>` : ""}
    </div>
  `;
}

// PREVIEW (Map Lab): a basic infowindow for a single animal/environment report
// point. subtype/severity come straight off the record; date is the submitted day.

export function humanizeToken(value) {
  const text = String(value || "").replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

export function controlledMapLabel(prefix, value) {
  const canonicalValue = String(value || "");
  return copy(`${prefix}_${canonicalValue}`) || humanizeToken(canonicalValue);
}

export function formatReportDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";
  return formatReportMapDate(`${match[1]}-${match[2]}-${match[3]}`, language());
}

export function pointReportPopupHtml(props) {
  const title = copy(MAP_LAB_POINT_TITLE_KEYS[props.category] || "map_report");
  const subtype = controlledMapLabel("map_subtype", props.subtype);
  const severity = controlledMapLabel("map_severity", props.severity);
  const zip = String(props.zip_code || "");
  const date = formatReportDate(props.submitted_at);
  const rows = [];
  if (subtype) rows.push(`<div class="zip-popup-total">${escapeHtml(subtype)}</div>`);
  if (severity) {
    rows.push(`<div class="zip-popup-sub">${escapeHtml(formatCopy("map_severity", { severity }))}</div>`);
  }
  const meta = [zip ? `${escapeHtml(copy("map_zip"))} ${escapeHtml(zip)}` : "", escapeHtml(date)].filter(Boolean).join(" &middot; ");
  if (meta) rows.push(`<div class="zip-popup-sub">${meta}</div>`);
  return `
    <div class="zip-popup">
      <strong>${escapeHtml(title)}</strong>
      ${rows.join("")}
    </div>
  `;
}

// PREVIEW (Map Lab ZIP shading): richer popup for the choropleth fill. People
// counts come from the joined fill feature; the cross-domain line comes from
// mapLabZipDetail(). Header is ZIP-only by design (no place-name lookup).

export function zipPolygonPopupHtml(props) {
  const zip = String(props.GEOID || props.ZCTA5 || "");
  const latest = formatReportDate(props.latest_submitted_at);
  const total = Number(props.total_count) || 0;
  const sick = Number(props.sick_count) || 0;
  const healthy = Number(props.healthy_count) || 0;
  const peopleSick = Number(props.people_sick_count) || 0;
  const population = Number(props.population) || 0;
  const metric = mapLabMagnitudeMetric();
  const suppressedRate = metric === "rate" && population < MAP_LAB_MIN_POPULATION;
  const popText = population > 0
    ? countCopy("map_resident_one", "map_resident_many", population, {
      count: population.toLocaleString(language() === "ES" ? "es-US" : "en-US"),
    })
    : copy("map_population_unavailable");

  let peopleBlock;
  if (total > 0) {
    const share = Math.round((sick / total) * 100);
    const per1k = population > 0 ? (total / population) * 1000 : null;
    const volume =
      metric === "rate" && per1k != null && !suppressedRate
        ? `${escapeHtml(reportCountText(total))} &middot; ${escapeHtml(formatCopy("map_per_thousand", { count: per1k.toFixed(per1k >= 10 ? 0 : 1) }))}`
        : escapeHtml(reportCountText(total));
    peopleBlock = `
      <div class="zip-popup-share">
        <span class="zip-popup-swatch" style="background:${sickRateCssColor(sick / total)}"></span>${escapeHtml(formatCopy("map_sick_share", { percent: share }))}
      </div>
      <div class="zip-popup-split"><span class="zip-popup-sick">${sick} ${escapeHtml(copy("map_sick"))}</span> &middot; <span class="zip-popup-healthy">${healthy} ${escapeHtml(copy("map_healthy_lower"))}</span></div>
      <div class="zip-popup-total">${volume}</div>
      <div class="zip-popup-sub">${escapeHtml(countCopy(
        "map_people_reported_sick_one",
        "map_people_reported_sick_many",
        peopleSick
      ))}</div>
      ${suppressedRate ? `<div class="zip-popup-note">${escapeHtml(copy("map_insufficient_population"))}</div>` : ""}
    `;
  } else {
    peopleBlock = `<div class="zip-popup-sub">${escapeHtml(copy("map_no_people_reports"))}</div>`;
  }

  return `
    <div class="zip-popup">
      <div class="zip-popup-head">
        <strong>${escapeHtml(copy("map_zip"))} ${escapeHtml(zip)}</strong>
        <span class="zip-popup-pop">${escapeHtml(popText)}</span>
      </div>
      ${peopleBlock}
      ${latest ? `<div class="zip-popup-sub">${escapeHtml(formatCopy("map_latest_report", { date: latest }))}</div>` : ""}
      ${zipCrossDomainLine(mapLabZipDetail(zip))}
    </div>
  `;
}

// PREVIEW: the animal + environment summary line for the ZIP polygon popup.

export function zipCrossDomainLine(detail) {
  const animals = detail.animals.count;
  const environment = detail.environment.count;
  if (animals === 0 && environment === 0) return "";
  const highCount = detail.animals.high + detail.environment.high;
  const parts = [];
  if (animals > 0) {
    parts.push(escapeHtml(countCopy("map_animal_count_one", "map_animal_count_many", animals)));
  }
  if (environment > 0) {
    parts.push(escapeHtml(countCopy(
      "map_environment_count_one",
      "map_environment_count_many",
      environment
    )));
  }
  if (highCount > 0) {
    parts.push(escapeHtml(countCopy(
      "map_high_severity_count_one",
      "map_high_severity_count_many",
      highCount
    )));
  }
  return `
    <div class="zip-popup-divider"></div>
    <div class="zip-popup-cross">${parts.join(" &middot; ")}</div>
  `;
}

function reportCountText(count) {
  return countCopy("map_report_one", "map_report_many", count);
}

// PREVIEW: CSS equivalent of the sickRateColor() Mapbox ramp, so the popup swatch
// matches the polygon's fill hue for the same sick share.
